/**
 * Bulk entry: a table of sources, areas or equipment pasted straight from a spreadsheet.
 *
 * A real study arrives as a register with hundreds of rows, and typing them one at a time is the
 * reason classification work is slow. Nothing here touches the document: it reads the table, says
 * what it would create or change and what it could not read, and hands back whole entities for the
 * caller to apply in one edit. The engineer sees that list before anything happens.
 *
 * Headers are matched loosely ("Hole area (mm²)", "hole_area", "HoleArea" are the same column), and
 * a value may carry its own unit, take the one in the header, or fall back to the usual one.
 */
import { parseDelimited } from './csv';
import type { Area, AvailabilityLevel, Equipment, Grade, Id, ProjectDoc, ReleaseForm, ReleaseInput, Source, Substance, Vec3 } from '../model/types';
import { parseMarking } from '../model/marking';
import { parseQuantity, type Quantity } from '../ui/units';

export type BulkKind = 'sources' | 'areas' | 'equipment';

export interface BulkRow {
  /** Line in the pasted text, counting the header as line 1. */
  line: number;
  /** The tag or name the row is about, for the preview. */
  key: string;
  what: 'add' | 'update' | 'skip';
  /** Why it was skipped, or what was assumed. */
  issues: string[];
}

export interface BulkResult {
  kind: BulkKind;
  rows: BulkRow[];
  sources: Source[];
  areas: Area[];
  equipment: Equipment[];
  /** Problems with the table as a whole. */
  problems: string[];
  /** Header names that were understood, and those that were not. */
  recognised: string[];
  ignored: string[];
}

/** Excel puts tabs on the clipboard; a saved file is usually commas or semicolons. */
export function splitTable(text: string): string[][] {
  const first = text.replace(/^\uFEFF/, '').split(/\r?\n/, 1)[0] ?? '';
  const count = (c: string) => (first.split(c).length - 1);
  const sep = count('\t') > 0 ? '\t' : count(';') > count(',') ? ';' : ',';
  return parseDelimited(text, sep);
}

const norm = (h: string): string => h.toLowerCase().replace(/\(.*?\)/g, '').replace(/[^a-z]/g, '');
/** The unit written in the header, e.g. "Pressure (barg)". */
const headerUnit = (h: string): string | undefined => h.match(/\(([^)]*)\)/)?.[1]?.trim() || undefined;

class Columns {
  private readonly header: string[];
  readonly used = new Set<number>();
  constructor(header: string[]) { this.header = header; }
  find(...names: string[]): number {
    const i = this.header.findIndex((h) => names.includes(norm(h)));
    if (i >= 0) this.used.add(i);
    return i;
  }
  unit(i: number): string | undefined { return i >= 0 ? headerUnit(this.header[i] ?? '') : undefined; }
  recognised(): string[] { return this.header.filter((_, i) => this.used.has(i)).map((h) => h.trim()).filter(Boolean); }
  ignored(): string[] { return this.header.filter((h, i) => !this.used.has(i) && h.trim()).map((h) => h.trim()); }
}

const text = (row: string[], i: number): string | undefined => (i >= 0 ? row[i]?.trim() || undefined : undefined);

/**
  * Reads a number in whatever unit the cell carries, else the one in the header, else the usual one
  * for the quantity. A unit from the header is appended to the value rather than passed as the
  * default, so that "(m3/s)" goes through the same spelling rules as a unit someone typed, and an
  * unreadable one is reported instead of being quietly ignored.
  */
function quantityAt(row: string[], i: number, cols: Columns, quantity: Quantity, fallbackUnit: string, issues: string[], label: string): number | undefined {
  const raw = text(row, i);
  if (raw === undefined) return undefined;
  const fromHeader = cols.unit(i);
  const carriesUnit = /[^-+0-9.,eE\s]/.test(raw);
  const parsed = parseQuantity(carriesUnit || !fromHeader ? raw : `${raw} ${fromHeader}`, quantity, fallbackUnit);
  if ('error' in parsed) { issues.push(`${label}: ${parsed.error}`); return undefined; }
  return parsed.si;
}

const plain = (row: string[], i: number, issues: string[], label: string): number | undefined => {
  const raw = text(row, i);
  if (raw === undefined) return undefined;
  const n = Number(raw.replace(',', '.'));
  if (!Number.isFinite(n)) { issues.push(`${label}: "${raw}" is not a number.`); return undefined; }
  return n;
};

const yes = (v: string | undefined): boolean | undefined =>
  v === undefined ? undefined : /^(y|yes|true|1)$/i.test(v) ? true : /^(n|no|false|0)$/i.test(v) ? false : undefined;

const GRADES: Grade[] = ['continuous', 'primary', 'secondary'];
const FORMS: ReleaseForm[] = ['jet', 'diffusive', 'heavy'];
const LEVELS: AvailabilityLevel[] = ['good', 'fair', 'poor'];
/** Matches a word against a short list by its first letters, so "Sec" and "secondary" both work. */
const oneOf = <T extends string>(v: string | undefined, options: T[]): T | undefined => {
  if (!v) return undefined;
  const k = v.trim().toLowerCase();
  return options.find((o) => o === k) ?? options.find((o) => o.startsWith(k) && k.length >= 1);
};

/** What the header looks like, so the right reader can be offered without asking. */
export function detectKind(header: string[]): BulkKind | undefined {
  const names = new Set(header.map(norm));
  const has = (...n: string[]) => n.some((x) => names.has(x));
  if (has('substance', 'fluid', 'grade', 'gradeofrelease')) return 'sources';
  if (has('marking', 'exmarking', 'epl')) return 'equipment';
  if (has('ventilation', 'airflow', 'ventilationtype', 'airchanges')) return 'areas';
  if (has('name') && !has('tag')) return 'areas';
  if (has('tag')) return 'equipment';
  return undefined;
}

/**
 * A table of nothing but tags could be either register. When the header alone cannot say, the tags
 * themselves usually can: whichever register already holds them is the one being pasted.
 */
function refineKind(header: string[], body: string[][], doc: ProjectDoc): BulkKind | undefined {
  const guess = detectKind(header);
  const names = new Set(header.map(norm));
  const decided = ['substance', 'fluid', 'grade', 'gradeofrelease', 'marking', 'exmarking', 'epl', 'ventilation', 'airflow', 'ventilationtype', 'airchanges'].some((n) => names.has(n));
  if (guess !== 'equipment' || decided) return guess;
  const tagColumn = header.findIndex((h) => ['tag', 'equipmenttag', 'id', 'ref'].includes(norm(h)));
  if (tagColumn < 0) return guess;
  const tags = body.map((r) => r[tagColumn]?.trim()).filter((t): t is string => !!t);
  const inSources = tags.filter((t) => doc.sources.some((s) => s.tag === t)).length;
  const inEquipment = tags.filter((t) => doc.equipment.some((e) => e.tag === t)).length;
  return inSources > inEquipment ? 'sources' : guess;
}

const EMPTY: Omit<BulkResult, 'kind'> = { rows: [], sources: [], areas: [], equipment: [], problems: [], recognised: [], ignored: [] };

/** Reads a pasted table. Never throws: anything it cannot read becomes a problem on a row. */
export function readTable(raw: string, doc: ProjectDoc, newId: () => Id, kind?: BulkKind): BulkResult {
  const rows = splitTable(raw);
  const header = rows[0];
  if (!header || rows.length < 2) {
    return { ...EMPTY, kind: kind ?? 'sources', problems: ['Paste a header row and at least one row of data.'] };
  }
  const k = kind ?? refineKind(header, rows.slice(1), doc);
  if (!k) {
    return { ...EMPTY, kind: 'sources', problems: ['Could not tell what this table is. It needs a column the reader knows, such as Tag, Name, Substance or Marking.'] };
  }
  const body = rows.slice(1);
  if (k === 'sources') return readSources(header, body, doc, newId);
  if (k === 'areas') return readAreas(header, body, doc, newId);
  return readEquipment(header, body, doc, newId);
}

function finish(kind: BulkKind, cols: Columns, out: Omit<BulkResult, 'kind' | 'recognised' | 'ignored'>): BulkResult {
  return { ...out, kind, recognised: cols.recognised(), ignored: cols.ignored() };
}

function sourceColumns(cols: Columns) {
  return {
    tag: cols.find('tag', 'sourcetag', 'ref', 'reference', 'id'),
    desc: cols.find('description', 'desc', 'service'),
    area: cols.find('area', 'room', 'location'),
    substance: cols.find('substance', 'fluid', 'material', 'stream'),
    grade: cols.find('grade', 'gradeofrelease'),
    gradeBasis: cols.find('gradebasis', 'basisforthegrade'),
    x: cols.find('x', 'positionx', 'centrex', 'centerx', 'easting'),
    y: cols.find('y', 'positiony', 'centrey', 'centery', 'northing'),
    z: cols.find('z', 'positionz', 'elevation', 'height'),
    form: cols.find('form', 'formofrelease', 'releaseform'),
    model: cols.find('model', 'releasemodel'),
    pressure: cols.find('pressure', 'operatingpressure', 'sourcepressure'),
    temperature: cols.find('temperature', 'temp', 'gastemperature'),
    hole: cols.find('holearea', 'hole', 'holesize', 'crosssection'),
    cd: cols.find('cd', 'dischargecoefficient'),
    zfactor: cols.find('compressibility', 'zfactor'),
    dp: cols.find('pressuredifference', 'deltap', 'dp'),
    vapour: cols.find('vapourfraction', 'vaporfraction', 'flashfraction'),
    poolArea: cols.find('poolarea'),
    liquidTemp: cols.find('liquidtemperature'),
    rate: cols.find('releaserate', 'massrate', 'wg'),
    rateRef: cols.find('ratereference', 'releasereference'),
    notes: cols.find('notes', 'comment', 'comments'),
  };
}
type SourceCols = ReturnType<typeof sourceColumns>;

function readSources(header: string[], body: string[][], doc: ProjectDoc, newId: () => Id): BulkResult {
  const cols = new Columns(header);
  const c = sourceColumns(cols);
  const out = { rows: [] as BulkRow[], sources: [] as Source[], areas: [] as Area[], equipment: [] as Equipment[], problems: [] as string[] };
  if (c.tag < 0) out.problems.push('No "Tag" column, so no row can be identified.');
  const areaByName = new Map(doc.areas.map((a) => [a.name.trim().toLowerCase(), a]));
  const subByName = new Map<string, Substance>();
  for (const s of doc.substances) {
    subByName.set(s.name.trim().toLowerCase(), s);
    if (s.casNumber) subByName.set(s.casNumber.trim().toLowerCase(), s);
  }
  if (c.tag < 0) return finish('sources', cols, out);

  body.forEach((row, i) => {
    const line = i + 2;
    const issues: string[] = [];
    const tag = text(row, c.tag);
    if (!tag) { out.rows.push({ line, key: '', what: 'skip', issues: ['No tag.'] }); return; }
    const existing = doc.sources.find((s) => s.tag === tag);

    const areaName = text(row, c.area);
    const area = areaName ? areaByName.get(areaName.toLowerCase()) : undefined;
    if (areaName && !area) issues.push(`No area called "${areaName}" in this project.`);
    const subName = text(row, c.substance);
    const sub = subName ? subByName.get(subName.toLowerCase()) : undefined;
    if (subName && !sub) issues.push(`No substance called "${subName}" in this project.`);

    const areaId = area?.id ?? existing?.areaId;
    const substanceId = sub?.id ?? existing?.substanceId;
    if (!areaId) issues.push('A source of release belongs to an area; name one.');
    if (!substanceId) issues.push('A source of release needs a substance; name one.');
    if (!areaId || !substanceId) { out.rows.push({ line, key: tag, what: 'skip', issues }); return; }

    const home = doc.areas.find((a) => a.id === areaId);
    const fallback: Vec3 = existing?.position ?? [home?.box?.center[0] ?? 0, home?.box?.center[1] ?? 0, 1];
    const position: Vec3 = [
      quantityAt(row, c.x, cols, 'length', 'm', issues, 'X') ?? fallback[0],
      quantityAt(row, c.y, cols, 'length', 'm', issues, 'Y') ?? fallback[1],
      quantityAt(row, c.z, cols, 'length', 'm', issues, 'Z') ?? fallback[2],
    ];

    const gradeText = text(row, c.grade);
    const grade = oneOf(gradeText, GRADES) ?? existing?.grade ?? 'secondary';
    if (gradeText && !oneOf(gradeText, GRADES)) issues.push(`Grade "${gradeText}" not understood; took ${grade}.`);
    const formText = text(row, c.form);
    const substance = doc.substances.find((s) => s.id === substanceId);
    const form = oneOf(formText, FORMS) ?? existing?.releaseForm ?? ((substance?.relativeVapourDensity ?? 1) > 1 ? 'heavy' : 'diffusive');
    if (formText && !oneOf(formText, FORMS)) issues.push(`Form "${formText}" not understood; took ${form}.`);

    const release = readRelease(row, c, cols, substance, existing, issues);
    const source: Source = {
      ...(existing ? structuredClone(existing) : {}),
      id: existing?.id ?? newId(),
      tag,
      description: text(row, c.desc) ?? existing?.description ?? '',
      areaId,
      substanceId,
      position,
      grade,
      releaseForm: form,
      release,
    };
    const gradeBasis = text(row, c.gradeBasis);
    if (gradeBasis) source.gradeBasis = gradeBasis;
    const notes = text(row, c.notes);
    if (notes) source.notes = notes;

    out.sources.push(source);
    out.rows.push({ line, key: tag, what: existing ? 'update' : 'add', issues });
  });
  return finish('sources', cols, out);
}

/** Builds the release from whichever model columns are present, keeping anything already set. */
function readRelease(row: string[], c: SourceCols, cols: Columns, sub: Substance | undefined, existing: Source | undefined, issues: string[]): ReleaseInput {
  const named = text(row, c.model)?.toLowerCase();
  const model = named
    ? (['gas', 'liquid', 'pool', 'manual'] as const).find((m) => named.startsWith(m.slice(0, 3)))
    : existing?.release.model ?? (sub?.state === 'gas' ? 'gas' : 'liquid');
  if (named && !model) issues.push(`Release model "${named}" not understood.`);
  const kind = model ?? existing?.release.model ?? 'liquid';
  const was = existing?.release;
  const hole = quantityAt(row, c.hole, cols, 'holeArea', 'mm²', issues, 'Hole area');
  const cd = plain(row, c.cd, issues, 'Cd');

  if (kind === 'gas') {
    const old = was?.model === 'gas' ? was : undefined;
    // the old release goes in first so that anything the table does not mention survives, such as
    // the basis someone wrote for a vapour fraction
    return {
      ...old,
      model: 'gas',
      pressurePaAbs: quantityAt(row, c.pressure, cols, 'pressureAbs', 'barg', issues, 'Pressure') ?? old?.pressurePaAbs ?? 5e5 + 101_325,
      temperatureK: quantityAt(row, c.temperature, cols, 'temperature', '°C', issues, 'Temperature') ?? old?.temperatureK ?? 293.15,
      holeAreaM2: hole ?? old?.holeAreaM2 ?? 1e-6,
      cd: cd ?? old?.cd ?? 0.75,
      z: plain(row, c.zfactor, issues, 'Compressibility') ?? old?.z ?? 1,
    };
  }
  if (kind === 'pool') {
    const old = was?.model === 'pool' ? was : undefined;
    return {
      ...old,
      model: 'pool',
      areaM2: quantityAt(row, c.poolArea, cols, 'area', 'm²', issues, 'Pool area') ?? old?.areaM2 ?? 1,
      liquidTemperatureK: quantityAt(row, c.liquidTemp, cols, 'temperature', '°C', issues, 'Liquid temperature') ?? old?.liquidTemperatureK ?? 293.15,
    };
  }
  if (kind === 'manual') {
    const old = was?.model === 'manual' ? was : undefined;
    return {
      ...old,
      model: 'manual',
      massRateKgPerS: quantityAt(row, c.rate, cols, 'massRate', 'kg/s', issues, 'Release rate') ?? old?.massRateKgPerS ?? 0,
      reference: text(row, c.rateRef) ?? old?.reference ?? '',
    };
  }
  const old = was?.model === 'liquid' ? was : undefined;
  return {
    ...old,
    model: 'liquid',
    pressureDiffPa: quantityAt(row, c.dp, cols, 'pressureDiff', 'bar', issues, 'Pressure difference') ?? old?.pressureDiffPa ?? 3e5,
    holeAreaM2: hole ?? old?.holeAreaM2 ?? 1e-6,
    cd: cd ?? old?.cd ?? 0.75,
    vapourFraction: quantityAt(row, c.vapour, cols, 'fraction', '', issues, 'Vapour fraction') ?? old?.vapourFraction ?? 0.02,
  };
}

function readAreas(header: string[], body: string[][], doc: ProjectDoc, newId: () => Id): BulkResult {
  const cols = new Columns(header);
  const c = {
    name: cols.find('name', 'area', 'room', 'areaname'),
    kind: cols.find('kind', 'type', 'areatype'),
    length: cols.find('length', 'sizex', 'lengthx'),
    width: cols.find('width', 'sizey', 'widthy'),
    height: cols.find('height', 'sizez', 'heightz'),
    x: cols.find('x', 'centrex', 'centerx'),
    y: cols.find('y', 'centrey', 'centery'),
    rotation: cols.find('rotation', 'orientation', 'bearing'),
    vent: cols.find('ventilation', 'ventilationtype'),
    flow: cols.find('airflow', 'freshairflow', 'qa'),
    flowBasis: cols.find('airflowbasis', 'ventilationbasis', 'basis'),
    direction: cols.find('airmoves', 'flowdirection', 'direction'),
    velocity: cols.find('velocity', 'windspeed', 'uw', 'ventilationvelocity'),
    obstructed: cols.find('obstructed'),
    availability: cols.find('availability', 'availabilityofventilation'),
  };
  const out = { rows: [] as BulkRow[], sources: [] as Source[], areas: [] as Area[], equipment: [] as Equipment[], problems: [] as string[] };
  if (c.name < 0) {
    out.problems.push('No "Name" column, so no area can be identified.');
    return finish('areas', cols, out);
  }
  body.forEach((row, i) => {
    const line = i + 2;
    const issues: string[] = [];
    const name = text(row, c.name);
    if (!name) { out.rows.push({ line, key: '', what: 'skip', issues: ['No name.'] }); return; }
    const existing = doc.areas.find((a) => a.name.trim().toLowerCase() === name.toLowerCase());
    const kindText = text(row, c.kind);
    const kind: Area['kind'] = kindText ? (/^out/i.test(kindText) ? 'outdoor' : 'indoor') : existing?.kind ?? 'indoor';

    const base: Area = existing ? structuredClone(existing) : {
      id: newId(), name, kind,
      box: { center: [0, 0, 1.5], size: [6, 5, 3], yawRad: 0 },
      ventilation: kind === 'indoor'
        ? { kind: 'indoor', type: 'artificial-general', airFlowM3PerS: 0.5, flowDirection: [1, 0, 0], velocityReductionFactor: 1, inefficiencyF: 2 }
        : { kind: 'outdoor', velocityMPerS: 0.5, obstructed: false },
      availability: { level: kind === 'indoor' ? 'fair' : 'good' },
      ...(kind === 'indoor' ? { summation: { maxSimultaneousPrimary: 'all' as const } } : {}),
    };
    base.name = name;
    base.kind = kind;

    const box = base.box ?? { center: [0, 0, 1.5] as Vec3, size: [6, 5, 3] as Vec3, yawRad: 0 };
    const size: Vec3 = [
      quantityAt(row, c.length, cols, 'length', 'm', issues, 'Length') ?? box.size[0],
      quantityAt(row, c.width, cols, 'length', 'm', issues, 'Width') ?? box.size[1],
      quantityAt(row, c.height, cols, 'length', 'm', issues, 'Height') ?? box.size[2],
    ];
    const yaw = plain(row, c.rotation, issues, 'Rotation');
    base.box = {
      center: [
        quantityAt(row, c.x, cols, 'length', 'm', issues, 'Centre X') ?? box.center[0],
        quantityAt(row, c.y, cols, 'length', 'm', issues, 'Centre Y') ?? box.center[1],
        size[2] / 2,
      ],
      size,
      yawRad: yaw === undefined ? box.yawRad : (yaw * Math.PI) / 180,
    };

    if (kind === 'indoor') {
      const was = base.ventilation.kind === 'indoor' ? base.ventilation : undefined;
      const ventText = text(row, c.vent);
      const type = ventText
        ? (/local/i.test(ventText) ? 'artificial-local' : /nat/i.test(ventText) ? 'natural' : 'artificial-general')
        : was?.type ?? 'artificial-general';
      const dirText = text(row, c.direction)?.toLowerCase();
      const flowDirection: Vec3 = dirText?.includes('y') ? [0, 1, 0] : dirText?.includes('z') ? [0, 0, 1] : dirText?.includes('x') ? [1, 0, 0] : was?.flowDirection ?? [1, 0, 0];
      base.ventilation = {
        kind: 'indoor', type,
        airFlowM3PerS: quantityAt(row, c.flow, cols, 'airFlow', 'm³/h', issues, 'Air flow') ?? was?.airFlowM3PerS ?? 0.5,
        flowDirection,
        velocityReductionFactor: was?.velocityReductionFactor ?? 1,
        inefficiencyF: was?.inefficiencyF ?? 2,
        ...(text(row, c.flowBasis) ? { airFlowBasis: text(row, c.flowBasis)! } : was?.airFlowBasis ? { airFlowBasis: was.airFlowBasis } : {}),
      };
    } else {
      const was = base.ventilation.kind === 'outdoor' ? base.ventilation : undefined;
      base.ventilation = {
        kind: 'outdoor',
        velocityMPerS: quantityAt(row, c.velocity, cols, 'velocity', 'm/s', issues, 'Ventilation velocity') ?? was?.velocityMPerS ?? 0.5,
        obstructed: yes(text(row, c.obstructed)) ?? was?.obstructed ?? false,
        ...(was?.velocityBasis ? { velocityBasis: was.velocityBasis } : {}),
      };
      delete base.summation;
    }
    const level = oneOf(text(row, c.availability), LEVELS);
    if (level) base.availability = { ...base.availability, level };

    out.areas.push(base);
    out.rows.push({ line, key: name, what: existing ? 'update' : 'add', issues });
  });
  return finish('areas', cols, out);
}

function readEquipment(header: string[], body: string[][], doc: ProjectDoc, newId: () => Id): BulkResult {
  const cols = new Columns(header);
  const c = {
    tag: cols.find('tag', 'equipmenttag', 'id', 'ref'),
    desc: cols.find('description', 'desc'),
    area: cols.find('area', 'room', 'location'),
    x: cols.find('x', 'centrex', 'centerx', 'easting'),
    y: cols.find('y', 'centrey', 'centery', 'northing'),
    z: cols.find('z', 'centrez', 'centerz', 'elevation'),
    sx: cols.find('sizex', 'length'),
    sy: cols.find('sizey', 'width'),
    sz: cols.find('sizez', 'height'),
    marking: cols.find('marking', 'exmarking', 'certification'),
  };
  const out = { rows: [] as BulkRow[], sources: [] as Source[], areas: [] as Area[], equipment: [] as Equipment[], problems: [] as string[] };
  if (c.tag < 0) {
    out.problems.push('No "Tag" column, so no item can be identified.');
    return finish('equipment', cols, out);
  }
  const areaByName = new Map(doc.areas.map((a) => [a.name.trim().toLowerCase(), a.id]));
  body.forEach((row, i) => {
    const line = i + 2;
    const issues: string[] = [];
    const tag = text(row, c.tag);
    if (!tag) { out.rows.push({ line, key: '', what: 'skip', issues: ['No tag.'] }); return; }
    const existing = doc.equipment.find((e) => e.tag === tag);
    const base: Equipment = existing ? structuredClone(existing) : {
      id: newId(), tag, description: '',
      box: { center: [0, 0, 0.5], size: [0.4, 0.4, 0.4], yawRad: 0 },
      ex: { kind: 'non-ex' },
    };
    base.description = text(row, c.desc) ?? base.description;
    base.box = {
      ...base.box,
      center: [
        quantityAt(row, c.x, cols, 'length', 'm', issues, 'X') ?? base.box.center[0],
        quantityAt(row, c.y, cols, 'length', 'm', issues, 'Y') ?? base.box.center[1],
        quantityAt(row, c.z, cols, 'length', 'm', issues, 'Z') ?? base.box.center[2],
      ],
      size: [
        quantityAt(row, c.sx, cols, 'length', 'm', issues, 'Size X') ?? base.box.size[0],
        quantityAt(row, c.sy, cols, 'length', 'm', issues, 'Size Y') ?? base.box.size[1],
        quantityAt(row, c.sz, cols, 'length', 'm', issues, 'Size Z') ?? base.box.size[2],
      ],
    };
    const marking = text(row, c.marking);
    if (marking !== undefined) {
      if (/^(non[- ]?ex|not ex|none|-)$/i.test(marking)) base.ex = { kind: 'non-ex' };
      else {
        const p = parseMarking(marking);
        if (p.epl && p.gasGroup) {
          base.ex = { kind: 'ex', epl: p.epl, gasGroup: p.gasGroup, ...(p.tClass ? { tClass: p.tClass } : {}), ...(p.maxSurfaceTempC ? { maxSurfaceTempC: p.maxSurfaceTempC } : {}), marking };
        } else {
          base.ex = { kind: 'non-ex' };
          issues.push(`Could not read a protection level and gas group from "${marking}"; recorded as not Ex.`);
        }
      }
    }
    const areaName = text(row, c.area);
    if (areaName) {
      const id = areaByName.get(areaName.toLowerCase());
      if (id) base.areaId = id;
      else issues.push(`No area called "${areaName}" in this project.`);
    }
    out.equipment.push(base);
    out.rows.push({ line, key: tag, what: existing ? 'update' : 'add', issues });
  });
  return finish('equipment', cols, out);
}

/** One line saying what applying the table would do. */
export function summarise(r: BulkResult): string {
  const n = (what: BulkRow['what']) => r.rows.filter((x) => x.what === what).length;
  const noun = r.kind === 'sources' ? 'source' : r.kind === 'areas' ? 'area' : 'item';
  const parts: string[] = [];
  if (n('add')) parts.push(`add ${n('add')} ${noun}${n('add') === 1 ? '' : 's'}`);
  if (n('update')) parts.push(`update ${n('update')}`);
  if (n('skip')) parts.push(`skip ${n('skip')}`);
  return parts.length ? parts.join(', ') : 'nothing to do';
}
